import { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
} from "@chakra-ui/react";

export function useIdleTimeout({ idleSeconds = 90, countdownSeconds = 20, onTimeout, enabled = true }) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(countdownSeconds);
  const idleTimer = useRef(null);
  const countdownTimer = useRef(null);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const resetIdleTimer = useCallback(() => {
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
      countdownTimer.current = null;
    }
    setShowWarning(false);
    setSecondsLeft(countdownSeconds);

    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (!enabled) return;
    idleTimer.current = setTimeout(() => {
      setShowWarning(true);
    }, idleSeconds * 1000);
  }, [idleSeconds, countdownSeconds, enabled]);

  useEffect(() => {
    if (!showWarning) return;
    setSecondsLeft(countdownSeconds);
    countdownTimer.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimer.current);
          countdownTimer.current = null;
          onTimeoutRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (countdownTimer.current) clearInterval(countdownTimer.current);
    };
  }, [showWarning, countdownSeconds]);

  useEffect(() => {
    const events = ["touchstart", "click", "keypress"];
    events.forEach((e) => window.addEventListener(e, resetIdleTimer));
    resetIdleTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (countdownTimer.current) clearInterval(countdownTimer.current);
    };
  }, [resetIdleTimer]);

  const WarningModal = (
    <Modal isOpen={showWarning} onClose={resetIdleTimer} isCentered>
      <ModalOverlay />
      <ModalContent rounded="lg" p={8}>
        <ModalHeader fontSize="2xl" fontWeight={700} color="gray.800">
          Are you still there?
        </ModalHeader>
        <ModalBody>
          <Text fontSize="xl" color="gray.600">
            This kiosk will reset in {secondsLeft} seconds.
          </Text>
        </ModalBody>
        <ModalFooter flexDirection="column" gap={3}>
          <Button
            size="kiosk"
            w="full"
            onClick={resetIdleTimer}
          >
            I'm still here
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  return { WarningModal };
}
